import React from 'react';
import { motion } from 'framer-motion';
import { Navigation } from './Navigation';
interface AboutUsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}
export function AboutUs({
  activeTab,
  onTabChange
}: AboutUsProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} transition={{
    duration: 0.6,
    ease: 'easeOut'
  }} className="w-full min-h-screen bg-white" data-id="element-36">
      <Navigation activeTab={activeTab} onTabChange={onTabChange} data-id="element-37" />

      {/* Hero Section */}
      <section className="px-6 py-32 md:py-40 bg-zinc-50 text-zinc-950 pt-32 md:pt-40" data-id="element-38">
        <div className="max-w-4xl mx-auto text-center" data-id="element-39">
          <motion.p className="text-sm tracking-[0.3em] uppercase mb-8 text-zinc-600" initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-40">
            Our Story
          </motion.p>
          <motion.h1 className="text-5xl md:text-7xl font-serif mb-8 leading-tight" style={{
          fontFamily: 'Playfair Display, serif'
        }} initial={{
          y: 30,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-41">
            About Us
          </motion.h1>
          <motion.p className="text-lg md:text-xl text-zinc-600 leading-relaxed max-w-2xl mx-auto" initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-42">
            Pioneering cellular wellness and longevity through science-backed
            NAD+ solutions.
          </motion.p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="px-6 py-32 md:py-40 bg-white" data-id="element-43">
        <div className="max-w-7xl mx-auto" data-id="element-44">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-45">
            <div data-id="element-46">
              <p className="text-sm tracking-[0.3em] uppercase mb-6 text-zinc-600" data-id="element-47">
                Our Mission
              </p>
              <h2 className="text-4xl md:text-5xl font-serif mb-6 text-zinc-950" style={{
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-48">
                Redefining the Standard
              </h2>
              <p className="text-lg text-zinc-600 leading-relaxed mb-6" data-id="element-49">
                To deliver an unparalleled, high-end wellness experience that
                merges cutting-edge medical advancements with luxury lifestyle
                services—helping our clients look better, feel better, and live
                better with confidence and clarity.
              </p>
            </div>
            <div className="aspect-[4/5] bg-zinc-200 overflow-hidden" data-id="element-50">
              <img src="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=800&q=80" alt="Research laboratory" className="w-full h-full object-cover" data-id="element-51" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="px-6 py-32 md:py-40 bg-zinc-50" data-id="element-52">
        <div className="max-w-7xl mx-auto" data-id="element-53">
          <div className="text-center mb-20" data-id="element-54">
            <p className="text-sm tracking-[0.3em] uppercase mb-6 text-zinc-600" data-id="element-55">
              Our Services
            </p>
            <h2 className="text-4xl md:text-6xl font-serif text-zinc-950" style={{
            fontFamily: 'Playfair Display, serif'
          }} data-id="element-56">
              Elite Science-Based Services
            </h2>
            <p className="text-lg text-zinc-600 leading-relaxed max-w-3xl mx-auto mt-6" data-id="element-57">
              Celivion Longevity offers a curated suite of elite, science-based
              services, each designed to elevate both the internal and external
              dimensions of health.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto" data-id="element-58">
            {[{
            title: 'NAD⁺ & Cellular Longevity Therapies',
            description: 'Replenish, restore, and revitalize at the cellular level through physician-supervised NAD⁺ infusions and mitochondrial repair protocols.'
          }, {
            title: 'Precision Nutrition & Longevity Meal Programs',
            description: 'Scientifically designed nutrition systems tailored to metabolic health, anti-inflammation, and sustainable vitality—crafted for high-performance lifestyles.'
          }, {
            title: 'Medical Aesthetics & Regenerative Beauty',
            description: 'Advanced aesthetic treatments that enhance natural beauty, restore youthfulness, and promote long-term skin regeneration.'
          }, {
            title: 'Genetic & Epigenetic Analysis',
            description: "Discover your body's unique blueprint with comprehensive genetic testing that guides personalized longevity plans and lifestyle optimization."
          }].map((value, index) => <div key={index} className="bg-white p-8 border border-zinc-200" data-id="element-59">
                <h3 className="text-2xl font-serif mb-4 text-zinc-950" style={{
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-60">
                  {value.title}
                </h3>
                <p className="text-zinc-600 leading-relaxed" data-id="element-61">
                  {value.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="px-6 py-32 md:py-40 bg-white" data-id="element-62">
        <div className="max-w-4xl mx-auto text-center" data-id="element-63">
          <p className="text-sm tracking-[0.3em] uppercase mb-6 text-zinc-600" data-id="element-64">
            Our Vision
          </p>
          <h2 className="text-4xl md:text-5xl font-serif mb-8 text-zinc-950 leading-tight" style={{
          fontFamily: 'Playfair Display, serif'
        }} data-id="element-65">
            Leading the Future of Longevity Medicine
          </h2>
          <p className="text-lg text-zinc-600 leading-relaxed" data-id="element-66">
            To become the leading authority in luxury longevity
            medicine—bringing together world-class medical expertise,
            personalized wellness strategies, and a refined, modern aesthetic
            that empowers our clients to thrive physically, mentally, and
            emotionally.
          </p>
        </div>
      </section>

      {/* Experience Section */}
      <section className="px-6 py-32 md:py-40 bg-zinc-50" data-id="element-67">
        <div className="max-w-5xl mx-auto" data-id="element-68">
          <div className="text-center mb-16" data-id="element-69">
            <h2 className="text-4xl md:text-5xl font-serif mb-8 text-zinc-950 leading-tight" style={{
            fontFamily: 'Playfair Display, serif'
          }} data-id="element-70">
              The Celivion Experience
            </h2>
            <p className="text-lg text-zinc-600 leading-relaxed max-w-3xl mx-auto" data-id="element-71">
              Every detail of the Celivion Longevity journey is intentionally
              designed—elegant, discreet, elevated, and deeply personalized.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-72">
            {['Concierge-level care', 'Medically grounded protocols', 'Scientifically validated treatments', 'A luxurious, holistic wellness experience'].map((item, index) => <div key={index} className="flex items-start gap-4" data-id="element-73">
                <div className="w-1.5 h-1.5 bg-zinc-950 rounded-full mt-2.5 flex-shrink-0" data-id="element-74" />
                <p className="text-lg text-zinc-600" data-id="element-75">{item}</p>
              </div>)}
          </div>

          <p className="text-lg text-zinc-600 leading-relaxed text-center max-w-3xl mx-auto border-t border-zinc-200 pt-12" data-id="element-76">
            Celivion Longevity represents a new paradigm—where health, beauty,
            and long-term vitality align with sophistication and modern luxury.
          </p>
        </div>
      </section>
    </motion.div>;
}